import React, { useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { Box, Button, Typography } from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { observer } from "mobx-react-lite";

import GreetingsMessage from "@/components/GreetingMessage";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { displayError, isInvalidError, showInvalidError } from "@/utils/errors";

import { useSchedule } from "./SchedulerProvider";
import CalendarView from "./Tabs/CalendarView";
import ScheduleListView from "./Tabs/ScheduleListView";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const createWorkScheduleMutation = graphql(`
  mutation CreateWorkSchedule($input: CreateWorkScheduleInput!) {
    createWorkSchedule(input: $input) {
      workSchedule {
        ...CoachWorkSchedule
      }
    }
  }
`);

const updateWorkScheduleMutation = graphql(`
  mutation UpdateWorkScheduleInput($input: UpdateWorkScheduleInput!) {
    updateWorkSchedule(input: $input) {
      coach {
        id
        workSchedule {
          ...CoachWorkSchedule
        }
      }
    }
  }
`);

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ height: "100vh" }}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const profilePanelQuery = graphql(`
  query MyProfile {
    coachViewer {
      id
      account {
        id
        email
      }
      profile {
        fullName
        avatarUrl
      }
    }
  }
`);

const SchedulerForm: React.FC = observer(() => {
  const snackbar = useGlobalSnackbar();
  const [tabValue, setTabValue] = useState<number>(0);
  const { data } = useQuery(profilePanelQuery);
  const coachFullname = data?.coachViewer.profile?.fullName;
  const [createWorkSchedule] = useMutation(createWorkScheduleMutation);
  const [updateWorkSchedule] = useMutation(updateWorkScheduleMutation);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const schedule = useSchedule();

  const onSubmitWorkSchedule = async () => {
    if (schedule.mode == "create") {
      try {
        const result = await createWorkSchedule({
          variables: {
            input: schedule.toCreateWorkScheduleInput(
              data?.coachViewer.id ?? ""
            ),
          },
        });
        if (result.data?.createWorkSchedule.workSchedule?.id) {
          await schedule.load();
        }
        snackbar.open("Successfully saved work schedule.");
      } catch (error: any) {
        snackbar.open(
          showInvalidError(
            error,
            "There is an error in your time schedule. Double check and fix it before submitting."
          )
        );
      }
    } else {
      try {
        const result = await updateWorkSchedule({
          variables: { input: schedule.toUpdateWorkScheduleInput() },
        });
        if (result.data?.updateWorkSchedule) {
          await schedule.load();
        }
        snackbar.open("Successfully updated work schedule");
      } catch (error: any) {
        snackbar.open(
          showInvalidError(
            error,
            "There is an error in your time schedule. Double check and fix it before submitting."
          )
        );
      }
    }
  };

  return (
    <>
      <GreetingsMessage fullName={coachFullname ?? "..."} />
      <Box
        display="flex"
        padding={4}
        flexDirection="row"
        justifyContent="space-between"
        width="100%"
        marginTop={2}
      >
        <Typography variant="h5">Manage Schedule</Typography>
        <Button
          type="submit"
          onClick={() => {
            onSubmitWorkSchedule();
          }}
          disabled={!schedule.isFormValid}
        >
          Save schedule
        </Button>
      </Box>
      <Box sx={{ width: "100%", marginTop: 0 }} padding={4}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            sx={{ backgroundColor: "#fff" }}
            value={tabValue}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            <Tab
              sx={{ textTransform: "none" }}
              label="List View"
              {...a11yProps(0)}
            />
            {Features.schedulerCalendarViewEnabled && (
              <Tab
                sx={{ textTransform: "none" }}
                label="Calendar View"
                {...a11yProps(1)}
              />
            )}
          </Tabs>
        </Box>
        <CustomTabPanel value={tabValue} index={0}>
          <ScheduleListView />
        </CustomTabPanel>
        {Features.schedulerCalendarViewEnabled && (
          <CustomTabPanel value={tabValue} index={1}>
            <CalendarView />
          </CustomTabPanel>
        )}
      </Box>
    </>
  );
});

export default SchedulerForm;
